import React, { useEffect, useState } from 'react';
import styles from './AddReport.module.scss';
import PropTypes from 'prop-types';

import {
  FormLabel,
  FormRow,
  FormSearchInput,
  FormSelectInput,
  FormServerAssetSearch,
  FormServerLooseItemSearch
} from 'components/FormComponents';
import DialogWrapper from 'components/DialogComponents/DialogWrapper';
import DialogHeader from 'components/DialogComponents/DialogHeader';
import { MainButton } from 'components/StyledComponents';
import ConfirmationPopup from 'components/ConfirmationPopup/ConfirmationPopup';

import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  getLastSavedGeneralReport,
  getLocationsAndProjects,
  getShortedReportTemplates,
  searchReportTemplateByLocationId,
  searchTemplateByAssetId,
  getTemplateLocations
} from 'actions/reportListActions';

import { getErrorsProperties } from 'helpers/ErrorValidator';
import { getUserFullName } from 'helpers/AppHelpers';

import { EXIST_REPORT } from 'constants/dialogPopupsData';

import { generateExistedReportUrl, generateUrl, REPORT_ENTITIES, REPORT_TYPES } from './helpers';
import { PRJPlate } from './PRJPlate';

/**
 * @typedef {Object} AddGeneralReportPopupProps
 * @property {boolean} open
 * @property {() => void} setOpen
 * @property {'Asset' | 'Location' | 'General'} reportType
 */

/**
 * @param {AddGeneralReportPopupProps} props
 */

const CustomOption = (option, label) => (
  <div className={styles.select_option}>
    <span>{label}</span>
    {option?.project?.id && <PRJPlate />}
  </div>
);

function AddGeneralReportPopup({ open, setOpen, reportType }) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [reportEntity, setReportEntity] = useState('');
  const [options, setOptions] = useState({});
  const [modalData, setModalData] = useState({});

  const methods = useForm({
    defaultValues: { template: {}, asset: {}, location: {}, looseItem: {} },
    mode: 'onChange'
  });
  const { getValues, setValue, formState, setError, clearErrors } = methods;
  const { errors } = formState;
  const isFormInvalid = !!Object.values(errors).filter(Boolean).length;

  useEffect(() => {
    if (!open) return;

    if (reportType === REPORT_TYPES.GENERAL) {
      dispatch(getShortedReportTemplates({ filters: { generalReportTypeName: ['General'] } })).then(
        (res) => setOptions((prev) => ({ ...prev, templates: res }))
      );
    }

    if (reportType === REPORT_TYPES.LOCATION) {
      dispatch(getLocationsAndProjects()).then((res) =>
        setOptions((prev) => ({
          ...prev,
          locations: res.map((el) => ({ ...el, id: el.index + 1 }))
        }))
      );
    }
  }, [open]);

  const { templates, locations } = options;

  const resetEntityFields = () => {
    setValue('asset', {});
    setValue('location', {});
    setValue('looseItem', {});
  };

  const selectTemplate = async ({ name, value }) => {
    clearErrors();
    setValue(name, value);

    if (!value?.id || reportType !== REPORT_TYPES.GENERAL) return;

    resetEntityFields();

    const templateReportEntity = value?.reportEntity?.name || '';

    if (!templateReportEntity) return setReportEntity('');

    setReportEntity(templateReportEntity);

    if (templateReportEntity !== REPORT_ENTITIES.LOCATION) return;

    const response = await dispatch(
      getTemplateLocations({ filters: { active: true, generalReportTemplateId: value.id } })
    );

    if (!response || !response?.items?.length)
      return setOptions((prev) => ({ ...prev, locations: [] }));

    const { items } = response;

    setOptions((prev) => ({ ...prev, locations: items }));
  };

  const selectAsset = (name, value) => {
    clearErrors(['template', 'asset', 'location']);
    setValue(name, value);

    if (value?.id) {
      dispatch(searchTemplateByAssetId(value.id)).then((res) => {
        setValue('template', res?.generalReportTemplates?.find(({ isDefault }) => isDefault) || {});
        setOptions((prev) => ({ ...prev, templates: res?.generalReportTemplates || [] }));
      });
    } else {
      setValue('template', {});
      setOptions((prev) => ({ ...prev, templates: [] }));
    }
  };

  const selectLocation = (name, value) => {
    clearErrors(['template', 'asset', 'location']);
    setValue(name, value);

    if (value?.name) {
      const { isProject, project, location } = value;
      const query = isProject ? { projectId: project.id } : { locationId: location.id };
      dispatch(searchReportTemplateByLocationId(query)).then((res) => {
        setValue('template', res?.find(({ isDefault }) => isDefault) || {});
        setOptions((prev) => ({ ...prev, templates: res || [] }));
      });
    } else {
      setValue('template', {});
      setOptions((prev) => ({ ...prev, templates: [] }));
    }
  };

  const selectEntityItem = (name, value) => {
    clearErrors(['asset', 'location', 'looseItem']);
    setValue(name, value);
  };

  const validateForm = () => {
    const { template, asset, location, looseItem } = getValues();

    const shouldCheck = {
      asset:
        reportType === REPORT_TYPES.ASSET ||
        (reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.ASSET),
      location:
        reportType === REPORT_TYPES.LOCATION ||
        (reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.LOCATION),
      looseItem: reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.LOOSE_ITEM
    };

    const fieldsToValidate = [
      {
        name: 'template',
        errorMessage: 'Report name field is required',
        isInvalid: !template?.id
      },
      {
        name: 'asset',
        errorMessage: 'Asset field is required',
        isInvalid: !asset?.id,
        shouldCheck: shouldCheck.asset
      },
      {
        name: 'location',
        errorMessage: 'Location field is required',
        isInvalid: !location?.id,
        shouldCheck: shouldCheck.location
      },
      {
        name: 'looseItem',
        errorMessage: 'Loose Item field is required',
        isInvalid: !looseItem?.id,
        shouldCheck: shouldCheck.looseItem
      }
    ];

    let isFormValid = true;
    fieldsToValidate.forEach(({ name, errorMessage, isInvalid, shouldCheck = true }) => {
      if (isInvalid && shouldCheck) {
        setError(name, getErrorsProperties(errorMessage), { shouldFocus: true });
        isFormValid = false;
      }
    });
    return isFormValid;
  };

  const navigateToReportCreation = () =>
    history.push(generateUrl(reportType, getValues(), reportEntity));

  const closePopup = () => setOpen(false);
  const applyPopup = () => {
    if (!validateForm()) return;

    if (reportType === REPORT_TYPES.LOCATION) {
      const { template, location } = getValues();
      const query = { generalReportTemplateId: template.id };

      if (location.isProject) {
        query.projectId = location.project.id;
      } else {
        query.locationId = location.location.id;
      }

      dispatch(getLastSavedGeneralReport(query)).then((res) => {
        if (!res?.lastSavedTodayGeneralReport?.id) {
          navigateToReportCreation();
          return;
        }

        const { lastSavedTodayGeneralReport } = res;
        const userName = getUserFullName(lastSavedTodayGeneralReport.createdByUser);
        setModalData({ ...EXIST_REPORT(userName), isOpened: true, reportData: res });
      });
    } else {
      navigateToReportCreation();
    }
  };

  const dismissModal = () => {
    setModalData({});
    history.push(generateExistedReportUrl(modalData.reportData.lastSavedTodayGeneralReport.id));
  };

  const agreeModal = () => {
    setModalData({});
    closePopup();

    navigateToReportCreation();
  };

  return (
    <DialogWrapper open={open} onClose={closePopup}>
      <div className={styles.popup}>
        <DialogHeader title="Create report" onClose={closePopup} />
        <FormProvider {...methods}>
          <div className={styles.form}>
            {reportType === REPORT_TYPES.ASSET && (
              <FormRow>
                <FormLabel required>Asset</FormLabel>
                <FormServerAssetSearch
                  name="asset"
                  onSelect={selectAsset}
                  getLabel={(option) => option?.drCode || ''}
                  getQuery={(searchQuery) => ({
                    pagination: { limit: 39, page: 1 },
                    filters: { isDeleted: false, includeUserCheck: false, searchQuery }
                  })}
                />
              </FormRow>
            )}
            {reportType === REPORT_TYPES.LOCATION && (
              <FormRow>
                <FormLabel required>Location</FormLabel>
                <FormSearchInput
                  name="location"
                  options={locations}
                  onSelect={selectLocation}
                  RenderOption={CustomOption}
                />
              </FormRow>
            )}
            <FormRow>
              <FormLabel required>Report name</FormLabel>
              <FormSelectInput
                name="template"
                options={templates || []}
                onSelect={selectTemplate}
                isDisabled={!templates?.length}
              />
            </FormRow>
            {reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.LOCATION && (
              <FormRow>
                <FormLabel required>Location</FormLabel>
                <FormSearchInput name="location" options={locations} onSelect={selectEntityItem} />
              </FormRow>
            )}
            {reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.ASSET && (
              <FormRow>
                <FormLabel required>Asset</FormLabel>
                <FormServerAssetSearch
                  name="asset"
                  onSelect={selectEntityItem}
                  getLabel={(option) => option?.drCode || ''}
                  getQuery={(searchQuery) => ({
                    pagination: { limit: 39, page: 1 },
                    filters: {
                      isDeleted: false,
                      includeUserCheck: false,
                      generalReportTemplateId: getValues('template')?.id || null,
                      searchQuery
                    }
                  })}
                />
              </FormRow>
            )}
            {reportType === REPORT_TYPES.GENERAL && reportEntity === REPORT_ENTITIES.LOOSE_ITEM && (
              <FormRow>
                <FormLabel required>Loose Item</FormLabel>
                <FormServerLooseItemSearch
                  name="looseItem"
                  onSelect={selectEntityItem}
                  getQuery={(searchQuery) => ({
                    filters: {
                      generalReportTemplateId: getValues('template')?.id || null,
                      searchQuery
                    }
                  })}
                />
              </FormRow>
            )}
          </div>
        </FormProvider>
        <div className={styles.footer}>
          <MainButton text="Cancel" action={closePopup} type="secondary" />
          <MainButton text="Create" action={applyPopup} type="primary" isDisabled={isFormInvalid} />
        </div>
      </div>

      <ConfirmationPopup data={modalData} onAgree={agreeModal} onDismiss={dismissModal} />
    </DialogWrapper>
  );
}

AddGeneralReportPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  reportType: PropTypes.string.isRequired
};

AddGeneralReportPopup.defaultProps = {
  open: false,
  reportType: 'General'
};

export { AddGeneralReportPopup };
